/**
 * capacity.ts — JOB-SOLD-01 (Seth 2026-09-28: "a lot of times the techs just size it according to what was
 * existing but often times they are under or oversized"). How big a unit is, read off what the pricebook
 * and the proposal already say — the pricebook has no capacity columns — and how it compares to the load calc.
 *
 * Sizes come from, in order: the AHRI match on the job (rated numbers), the description ("70K BTU",
 * "3 Ton"), then the model number:
 *   furnace input  the first 3-digit size after letters     EL297UH070XV36BK → 70K · A98USMV090C12SK → 90K
 *   furnace AFUE   the pricebook's "97% AFUE", else the series digits   ML193 → 93 · SL280 → 80 · A80 → 80
 *   blower         the 2 digits after the size (tons × 12, or CFM ÷ 100)  …070XV36 → 3 ton · …090C16 → 4 ton
 *   AC / heat pump the size code                             EL16KC1-036 → 3 ton · A7AC14F30P → 2.5 ton
 *   coil           "2.5 Ton" / "1.5-2 Ton", or 30/36 · 7EC36 · AC36
 *
 * The check is Manual S's range on NOMINAL size (Settings → Sold Jobs & Load Calc): AC 95–115% of the cooling
 * load, heat pump 100–125%, furnace output 100–140% of the heating load. It's a screen, not a design — the load
 * calc software's own Manual S is the final word, and an AHRI rating beats a nominal size when the job has one.
 */

export type EquipRole = "furnace" | "boiler" | "ac" | "heat_pump" | "coil" | "air_handler" | "mini_split" | "water_heater" | "other";

export interface UnitText {
  category?: string | null;
  equip_key?: string | null;
  kind?: string | null;
  model?: string | null;
  part_number?: string | null;
  description?: string | null;
  efficiency?: string | null;
}

export interface Capacity {
  role: EquipRole;
  /** Furnace / boiler input, Btu/h. */
  inputBtu?: number;
  afue?: number;
  /** Furnace / boiler output = input × AFUE (or the AHRI number). */
  outputBtu?: number;
  /** AC / heat pump nominal tons. */
  tons?: number;
  /** Cooling capacity, Btu/h (nominal tons × 12,000, or the AHRI number). */
  coolBtu?: number;
  /** Furnace / air-handler blower, tons of airflow. */
  blowerTons?: number;
  /** Coil range, tons. */
  coilTons?: [number, number];
  /** Where the number came from. */
  from: "ahri" | "description" | "model" | "none";
}

const TON_CODES = new Set([18, 24, 30, 36, 42, 48, 60]);
const FURNACE_SIZES = new Set([40, 44, 45, 50, 54, 60, 66, 70, 75, 80, 85, 88, 90, 100, 105, 110, 115, 120, 125, 132, 135, 140, 150]);
const upper = (s?: string | null) => (s ?? "").toUpperCase();

export function roleOf(u: UnitText): EquipRole {
  const k = (u.equip_key ?? "").toLowerCase();
  const byKey: Record<string, EquipRole> = {
    furnace: "furnace", boiler: "boiler", "heat-pump": "heat_pump", ac: "ac", coil: "coil",
    "air-handler": "air_handler", "mini-split": "mini_split", "water-heater": "water_heater",
  };
  if (byKey[k]) return byKey[k];
  // The category speaks first ("Coil" whose description says "AC/HP"), then the description.
  for (const text of [u.category ?? "", u.description ?? ""]) {
    const c = text.toLowerCase();
    if (!c.trim()) continue;
    if (/\bcoil\b/.test(c)) return /condenser coil|repair/.test(c) ? "other" : "coil";
    if (/\bfurnace\b/.test(c)) return "furnace";
    if (/\bboiler\b/.test(c)) return "boiler";
    if (/heat ?pump/.test(c)) return "heat_pump";
    if (/air ?handler/.test(c)) return "air_handler";
    if (/mini[- ]?split|ductless/.test(c)) return "mini_split";
    if (/water heater/.test(c)) return "water_heater";
    if (/air cond|condenser|^\s*a\/?c\b/.test(c)) return "ac";
  }
  return "other";
}

/** "70K BTU" · "88,000 BTU" · "44K" → Btu/h. */
function btuFromText(s: string): number | undefined {
  const m = s.match(/(\d{2,3}(?:\.\d)?)\s*K\s*(?:BTU|MBH)?\b/i) ?? s.match(/(\d{2,3}),?000\s*BTU/i);
  if (!m) return undefined;
  const n = Number(m[1]);
  return n >= 20 && n <= 400 ? Math.round(n * 1000) : undefined;
}

/** "3 Ton" · "2.5 Ton" · "1.5-2 Ton" → [lo, hi]. */
function tonsFromText(s: string): [number, number] | undefined {
  const m = s.match(/(\d(?:\.\d)?)\s*(?:-|–|to)\s*(\d(?:\.\d)?)\s*-?\s*tons?\b/i);
  if (m) {
    const a = Number(m[1]), b = Number(m[2]);
    if (a > 0 && b >= a && b <= 25) return [a, b];
  }
  const one = s.match(/(\d{1,2}(?:\.\d{1,2})?)\s*-?\s*tons?\b/i);
  if (one) {
    const t = Number(one[1]);
    if (t > 0 && t <= 25) return [t, t];
  }
  return undefined;
}

/** A cooling size code in a model → tons (EL16KC1-036, EL18KCVS036, A7AC14F30P, 4SCU13LB136P). */
function tonsFromModel(model: string): number | undefined {
  const m = upper(model).replace(/\s+/g, "");
  if (!m) return undefined;
  const tries: RegExp[] = [
    /-0?(18|24|30|36|42|48|60)(?=\D|$)/,          // Lennox: -036, -024-230
    /[A-Z]0(18|24|30|36|42|48|60)(?=[A-Z]*$|-)/,  // EL18KCVS036, ML14XC1036
    /[A-Z](18|24|30|36|42|48|60)[A-Z]{1,2}$/,     // Armstrong A7AC14F30P, 7EC36BX
    /1(18|24|30|36|42|48|60)[A-Z]?$/,             // Goodman-style 4SCU13LB136P
  ];
  for (const re of tries) {
    const x = m.match(re);
    if (x && TON_CODES.has(Number(x[1]))) return Number(x[1]) / 12;
  }
  return undefined;
}

/** A furnace's nominal input off its model: the first 3-digit size right after letters (EL297UH070XV36BK → 70). */
function furnaceInputFromModel(model: string): { kbtu: number; blowerTons?: number } | undefined {
  const m = upper(model).replace(/[\s-]+/g, "");
  const re = /[A-Z](\d{3})(?=[A-Z])/g;
  let x: RegExpExecArray | null;
  while ((x = re.exec(m))) {
    const n = Number(x[1]);
    if (!FURNACE_SIZES.has(n)) continue;
    // The blower code right after the size: Lennox tons×12 (XV36, E48), Armstrong CFM÷100 (C12, B16, D020).
    const rest = m.slice(x.index + 4);
    const b = rest.match(/^[A-Z]{1,2}0?(\d{2})/);
    let blowerTons: number | undefined;
    if (b) {
      const v = Number(b[1]);
      if (TON_CODES.has(v)) blowerTons = v / 12;
      else if ([8, 10, 12, 14, 16, 20].includes(v)) blowerTons = (v * 100) / 400;
    }
    return { kbtu: n, blowerTons };
  }
  return undefined;
}

/** AFUE off the pricebook text, the description, or the series digits (ML193 → 93, SL280 → 80, A98 → 98). */
function afueOf(u: UnitText): number | undefined {
  for (const s of [u.efficiency, u.description]) {
    const m = (s ?? "").match(/(\d{2}(?:\.\d)?)\s*%/);
    if (m) {
      const n = Number(m[1]);
      if (n >= 78 && n <= 99.9) return n;
    }
  }
  for (const s of [u.model, u.part_number]) {
    const m = upper(s).match(/^[A-Z]{1,4}(\d{2,3})/);
    if (!m) continue;
    const n = Number(m[1].slice(-2));
    if (n >= 78 && n <= 99) return n;
  }
  return undefined;
}

function coilTonsFromModel(model: string): [number, number] | undefined {
  const m = upper(model).replace(/\s+/g, "");
  const std = (n: string) => TON_CODES.has(Number(n));
  // 30/36 · 3642 (two sizes side by side) → a range.
  const pair = m.match(/(\d{2})\/(\d{2})/) ?? m.match(/(?:^|[A-Z])(18|24|30|36|42|48)(24|30|36|42|48|60)(?=[A-Z]|$)/);
  if (pair && std(pair[1]) && std(pair[2]) && Number(pair[2]) > Number(pair[1])) return [Number(pair[1]) / 12, Number(pair[2]) / 12];
  // One size after the series: 7EC36 · AC36 · C3330 · CX34-30 · CH33-24 · CH35-24A.
  const one = m.match(/(?:7E[CDH]|AC|CX34-?|CH33-?|CH35-?|C33-?|CX|CH|CK)(\d{2})(?=[A-Z-]|$)/) ?? m.match(/-(\d{2})[A-Z]?(?:-|$)/);
  if (one && std(one[1])) {
    const t = Number(one[1]) / 12;
    return [t, t];
  }
  return undefined;
}

/** How big one unit is. */
export function capacityOf(u: UnitText): Capacity {
  const role = roleOf(u);
  const desc = u.description ?? "";
  const models = [u.model, u.part_number].filter(Boolean) as string[];
  if (role === "furnace" || role === "boiler") {
    let from: Capacity["from"] = "none";
    let inputBtu: number | undefined;
    let blowerTons: number | undefined;
    for (const mdl of models) {
      const f = furnaceInputFromModel(mdl);
      if (f) { inputBtu = f.kbtu * 1000; blowerTons = f.blowerTons; from = "model"; break; }
    }
    if (inputBtu == null) {
      const fromDesc = btuFromText(desc);
      if (fromDesc) { inputBtu = fromDesc; from = "description"; }
      // A model number written inside the description ("Furnace SL280UH090V48B-05").
      if (inputBtu == null) {
        const f = furnaceInputFromModel(desc.replace(/^[A-Za-z]+\s+/, ""));
        if (f) { inputBtu = f.kbtu * 1000; blowerTons = f.blowerTons; from = "description"; }
      }
    }
    // A furnace's "3 Ton" in the pricebook is its blower, never its heat.
    const dt = tonsFromText(desc);
    if (dt && blowerTons == null) blowerTons = dt[1];
    const afue = afueOf(u);
    const outputBtu = inputBtu != null && afue != null ? Math.round((inputBtu * afue) / 100) : undefined;
    return { role, inputBtu, afue, outputBtu, blowerTons, from };
  }
  if (role === "ac" || role === "heat_pump") {
    const dt = tonsFromText(desc);
    let tons = dt ? dt[1] : undefined;
    let from: Capacity["from"] = dt ? "description" : "none";
    if (tons == null) {
      for (const mdl of [...models, desc]) {
        const t = tonsFromModel(mdl);
        if (t) { tons = t; from = "model"; break; }
      }
    }
    if (tons == null) {
      const b = btuFromText(desc);
      if (b && b >= 12000 && b <= 72000) { tons = b / 12000; from = "description"; }
    }
    return { role, tons, coolBtu: tons != null ? Math.round(tons * 12000) : undefined, from };
  }
  if (role === "coil") {
    const dt = tonsFromText(desc);
    if (dt) return { role, coilTons: dt, from: "description" };
    for (const mdl of [...models, desc]) {
      const t = coilTonsFromModel(mdl);
      if (t) return { role, coilTons: t, from: "model" };
    }
    return { role, from: "none" };
  }
  if (role === "air_handler") {
    const dt = tonsFromText(desc);
    if (dt) return { role, blowerTons: dt[1], from: "description" };
    for (const mdl of models) {
      const t = tonsFromModel(mdl);
      if (t) return { role, blowerTons: t, from: "model" };
    }
  }
  return { role, from: "none" };
}

/** "70K" · "2.5 Ton" · "Blower 3 Ton" chips for a unit. */
export function capacityChips(c: Capacity): string[] {
  const out: string[] = [];
  if (c.inputBtu != null) out.push(`${Math.round(c.inputBtu / 1000)}K BTU`);
  if (c.afue != null) out.push(`${c.afue}% AFUE`);
  if (c.outputBtu != null) out.push(`≈${Math.round(c.outputBtu / 1000)}K out`);
  if (c.tons != null) out.push(`${fmtTons(c.tons)} Ton`);
  if (c.coilTons) out.push(c.coilTons[0] === c.coilTons[1] ? `${fmtTons(c.coilTons[0])} Ton coil` : `${fmtTons(c.coilTons[0])}–${fmtTons(c.coilTons[1])} Ton coil`);
  if (c.blowerTons != null && (c.role === "furnace" || c.role === "air_handler")) out.push(`Blower ${fmtTons(c.blowerTons)} Ton`);
  return out;
}

export const fmtTons = (t: number) => (Number.isInteger(t) ? String(t) : t.toFixed(1).replace(/\.0$/, ""));
export const kbtu = (n: number) => `${Math.round(n / 100) / 10}K`.replace(/\.0K$/, "K");
export const btuh = (n: number) => `${Math.round(n).toLocaleString("en-US")} Btu/h`;

/* ── The sizing check ─────────────────────────────────────────────────────── */

export interface SizingRules {
  ac_min: number; ac_max: number;
  hp_min: number; hp_max: number;
  furnace_min: number; furnace_max: number;
}
/** ACCA Manual S limits (percent of the Manual J load). */
export const MANUAL_S: SizingRules = { ac_min: 95, ac_max: 115, hp_min: 100, hp_max: 125, furnace_min: 100, furnace_max: 140 };

export type Verdict = "right" | "over" | "under" | "unknown" | "no_load";

export interface SizingLine {
  key: "heat" | "cool";
  label: string;
  /** What's being checked, in words ("EL297UH070 · 70K BTU · 97%"). */
  units: string[];
  capacity?: number;
  load?: number;
  pct?: number;
  verdict: Verdict;
  range?: [number, number];
  /** Nominal sizes that land inside the range. */
  fits: string[];
  capFrom?: Capacity["from"];
}

const COOL_TONS = [1.5, 2, 2.5, 3, 3.5, 4, 5];
const FURNACE_KBTU = [40, 45, 60, 70, 80, 90, 100, 110, 120, 135];

function verdictOf(pct: number | undefined, lo: number, hi: number): Verdict {
  if (pct == null) return "unknown";
  if (pct < lo / 100 - 0.0005) return "under";
  if (pct > hi / 100 + 0.0005) return "over";
  return "right";
}

export interface UnitForCheck extends UnitText { qty?: number | null; label: string }
export interface AhriNumbers { system_type: string | null; cooling_btuh: number | null; heating_btuh: number | null }

/** Heating and cooling, each checked against the load (units of one role add up — two furnaces = one total). */
export function sizingCheck(
  units: UnitForCheck[],
  load: { heat?: number | null; cool?: number | null },
  rules: SizingRules = MANUAL_S,
  ahri: AhriNumbers[] = [],
): SizingLine[] {
  const caps = units.map((u) => ({ u, c: capacityOf(u), q: Math.max(1, Number(u.qty) || 1) }));
  const lines: SizingLine[] = [];

  // Heating — furnaces and boilers (a heat pump's heat depends on the balance point; the software checks that).
  const heaters = caps.filter((x) => x.c.role === "furnace" || x.c.role === "boiler");
  if (heaters.length) {
    const ahriHeat = ahri.find((a) => a.system_type === "furnace" && a.heating_btuh)?.heating_btuh ?? null;
    const known = heaters.every((x) => x.c.outputBtu != null);
    const capacity = ahriHeat && heaters.length === 1 ? ahriHeat : known ? heaters.reduce((s, x) => s + (x.c.outputBtu ?? 0) * x.q, 0) : undefined;
    const L = load.heat ?? undefined;
    const pct = capacity != null && L ? capacity / L : undefined;
    const afue = heaters[0].c.afue ?? 96;
    const fits = L
      ? FURNACE_KBTU.filter((k) => {
          const out = (k * 1000 * afue) / 100;
          return out >= (L * rules.furnace_min) / 100 && out <= (L * rules.furnace_max) / 100;
        }).map((k) => `${k}K`)
      : [];
    lines.push({
      key: "heat", label: "Heating",
      units: heaters.map((x) => x.u.label),
      capacity, load: L, pct,
      verdict: !L ? "no_load" : verdictOf(pct, rules.furnace_min, rules.furnace_max),
      range: L ? [Math.round((L * rules.furnace_min) / 100), Math.round((L * rules.furnace_max) / 100)] : undefined,
      fits,
      capFrom: ahriHeat && heaters.length === 1 ? "ahri" : heaters[0].c.from,
    });
  }

  // Cooling — air conditioners and heat pumps.
  const coolers = caps.filter((x) => x.c.role === "ac" || x.c.role === "heat_pump");
  if (coolers.length) {
    const hp = coolers.some((x) => x.c.role === "heat_pump");
    const lo = hp ? rules.hp_min : rules.ac_min;
    const hi = hp ? rules.hp_max : rules.ac_max;
    const ahriCool = ahri.find((a) => a.system_type !== "furnace" && a.cooling_btuh)?.cooling_btuh ?? null;
    const known = coolers.every((x) => x.c.coolBtu != null);
    const capacity = ahriCool && coolers.length === 1 ? ahriCool : known ? coolers.reduce((s, x) => s + (x.c.coolBtu ?? 0) * x.q, 0) : undefined;
    const L = load.cool ?? undefined;
    const pct = capacity != null && L ? capacity / L : undefined;
    const fits = L ? COOL_TONS.filter((t) => t * 12000 >= (L * lo) / 100 && t * 12000 <= (L * hi) / 100).map((t) => `${fmtTons(t)} Ton`) : [];
    lines.push({
      key: "cool", label: hp ? "Cooling (Heat Pump)" : "Cooling",
      units: coolers.map((x) => x.u.label),
      capacity, load: L, pct,
      verdict: !L ? "no_load" : verdictOf(pct, lo, hi),
      range: L ? [Math.round((L * lo) / 100), Math.round((L * hi) / 100)] : undefined,
      fits,
      capFrom: ahriCool && coolers.length === 1 ? "ahri" : coolers[0].c.from,
    });
  }
  return lines;
}

/** Do the pieces fit each other? The furnace blower and the coil against the outdoor unit. */
export function matchNotes(units: UnitText[]): { ok: boolean; text: string }[] {
  const caps = units.map(capacityOf);
  const out: { ok: boolean; text: string }[] = [];
  const outdoor = caps.find((c) => (c.role === "ac" || c.role === "heat_pump") && c.tons != null);
  if (!outdoor?.tons) return out;
  const t = outdoor.tons;
  const blower = caps.find((c) => (c.role === "furnace" || c.role === "air_handler") && c.blowerTons != null);
  if (blower?.blowerTons != null) {
    out.push(blower.blowerTons + 0.01 >= t
      ? { ok: true, text: `Blower moves ${fmtTons(blower.blowerTons)} tons — enough for the ${fmtTons(t)} ton outdoor unit` }
      : { ok: false, text: `Blower moves ${fmtTons(blower.blowerTons)} tons — short for the ${fmtTons(t)} ton outdoor unit` });
  }
  const coil = caps.find((c) => c.role === "coil" && c.coilTons);
  if (coil?.coilTons) {
    const [lo, hi] = coil.coilTons;
    const ok = t >= lo - 0.01 && t <= hi + 0.51;
    const size = lo === hi ? `${fmtTons(lo)} ton` : `${fmtTons(lo)}–${fmtTons(hi)} ton`;
    out.push({ ok, text: ok ? `Coil is ${size} — matches the ${fmtTons(t)} ton outdoor unit` : `Coil is ${size} — the outdoor unit is ${fmtTons(t)} tons` });
  }
  return out;
}

/** The model's series without its size — the other sizes of the same line share it
 *  (EL297UH070XV36BK → "EL297UH", EL16KC1-036 → "EL16KC1-", A7AC14F30P → "A7AC14F"). */
export function seriesPrefix(model: string | null | undefined, role: EquipRole): string | null {
  const m = upper(model).replace(/\s+/g, "");
  if (!m) return null;
  if (role === "furnace" || role === "boiler") {
    const re = /[A-Z](\d{3})(?=[A-Z])/g;
    let x: RegExpExecArray | null;
    while ((x = re.exec(m))) {
      if (FURNACE_SIZES.has(Number(x[1]))) return m.slice(0, x.index + 1);
    }
    return null;
  }
  if (role === "ac" || role === "heat_pump") {
    const dash = m.match(/^(.*?-)0?(18|24|30|36|42|48|60)(?=\D|$)/);
    if (dash) return dash[1];
    const zero = m.match(/^(.*?[A-Z])0(18|24|30|36|42|48|60)(?=[A-Z]*$|-)/);
    if (zero) return zero[1];
    const arm = m.match(/^(.*?[A-Z])(18|24|30|36|42|48|60)[A-Z]{1,2}$/);
    if (arm) return arm[1];
  }
  return null;
}
